/** Ignore small client/server clock differences when treating JWT exp as stale. */
const JWT_CLOCK_SKEW_LEEWAY_SEC = 60;

export function isJwtExpiredPayload(payload) {
  if (!payload || payload.exp == null) return false;
  const nowSec = Date.now() / 1000;
  return payload.exp < nowSec - JWT_CLOCK_SKEW_LEEWAY_SEC;
}

export function parseJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  token = token.trim();
  if (!token) return null;

  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return null;

  try {
    const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payloadBase64Padded =
      payloadBase64 + '='.repeat((4 - (payloadBase64.length % 4)) % 4);
    const binary = atob(payloadBase64Padded);
    // JWT payload is UTF-8; atob alone breaks non‑Latin names in claims.
    if (typeof TextDecoder !== 'undefined') {
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const json = new TextDecoder('utf-8').decode(bytes);
      return JSON.parse(json);
    }
    return JSON.parse(binary);
  } catch (error) {
    return null;
  }
}

export function clearStoredAuth() {
  [
    'token',
    'name',
    'email',
    'avatar',
    'school',
    'role',
    'gender',
    'grade',
  ].forEach((key) => localStorage.removeItem(key));
}

export function hasValidStoredToken() {
  const token = localStorage.getItem('token');
  if (!token) return false;

  const payload = parseJwtPayload(token);
  if (!payload) {
    clearStoredAuth();
    return false;
  }

  if (isJwtExpiredPayload(payload)) {
    clearStoredAuth();
    return false;
  }

  return true;
}

export function syncStoredAuthFromToken(token = localStorage.getItem('token')) {
  if (!token) {
    clearStoredAuth();
    return null;
  }

  const payload = parseJwtPayload(token);
  if (!payload) {
    clearStoredAuth();
    return null;
  }

  if (isJwtExpiredPayload(payload)) {
    clearStoredAuth();
    return null;
  }

  localStorage.setItem('token', token);

  const fields = {
    name: payload.name,
    email: payload.email,
    avatar: payload.avatar,
    school: payload.school,
    role: payload.role,
    gender: payload.gender,
    grade: payload.grade,
  };

  Object.entries(fields).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
  });

  return payload;
}
